// Builds the `voice` block of a Vapi call config from an assistant row.
// Kept free of Supabase imports so it can be unit-tested on its own.

// Model list from Vapi's CartesiaVoice schema (api.vapi.ai/api-json).
export const CARTESIA_MODELS = [
  'sonic-3.5', 'sonic-3.5-2026-05-04',
  'sonic-3', 'sonic-3-2026-01-12', 'sonic-3-2025-10-27',
  'sonic-2', 'sonic-2-2025-06-11',
  'sonic-english', 'sonic-multilingual', 'sonic-preview', 'sonic',
];

// generationConfig ranges; Vapi rejects values outside them.
export const CARTESIA_SPEED_RANGE = { min: 0.6, max: 1.5 };
export const CARTESIA_VOLUME_RANGE = { min: 0.5, max: 2 };

const clamp = (n, { min, max }) => Math.min(max, Math.max(min, n));

// Vapi validates the voice object per provider and rejects fields a provider
// doesn't define. A Cartesia voice has no top-level speed/stability/similarity
// (those are ElevenLabs-style knobs), so tuning left on the row from a previous
// provider must not be sent. Its own tuning lives in nested objects instead.
function addCartesiaTuning(voiceConfig, assistantRecord) {
  // Vapi documents generationConfig as sonic-3 only. sonic-3.5 is accepted too
  // (verified live); with no model set Vapi picks one for the voice, so
  // there's nothing to check against and it's left out.
  if ((assistantRecord.voice_model || '').startsWith('sonic-3')) {
    const generationConfig = {};
    const speed = parseFloat(assistantRecord.voice_speed);
    const volume = parseFloat(assistantRecord.voice_volume);
    if (!Number.isNaN(speed) && speed !== 1) generationConfig.speed = clamp(speed, CARTESIA_SPEED_RANGE);
    if (!Number.isNaN(volume) && volume !== 1) generationConfig.volume = clamp(volume, CARTESIA_VOLUME_RANGE);
    if (Object.keys(generationConfig).length > 0) voiceConfig.generationConfig = generationConfig;
  }

  // Emotion is deliberately not sent. Vapi only exposes Cartesia's legacy
  // experimentalControls.emotion ("anger:high"), which had no audible effect
  // on any model we tried, and it rejects sonic-3's generationConfig.emotion.
  // vapi_assistants.voice_emotion exists but is unused.
}

export function buildVapiVoiceConfig(assistantRecord) {
  const provider = assistantRecord.voice_provider || 'vapi';
  const voiceConfig = {
    provider,
    voiceId: assistantRecord.voice_id || 'Lily',
    chunkPlan: {
      enabled: true
    }
  };

  if (assistantRecord.voice_model) voiceConfig.model = assistantRecord.voice_model;

  if (provider === 'cartesia') {
    addCartesiaTuning(voiceConfig, assistantRecord);
    return voiceConfig;
  }

  if (assistantRecord.voice_speed) voiceConfig.speed = parseFloat(assistantRecord.voice_speed);

  // Only add stability and similarityBoost for ElevenLabs (NOT for vapi)
  // These parameters cause issues with VAPI's voice provider
  if (provider !== 'vapi') {
    if (assistantRecord.voice_stability) voiceConfig.stability = parseFloat(assistantRecord.voice_stability);
    if (assistantRecord.voice_similarity_boost) voiceConfig.similarityBoost = parseFloat(assistantRecord.voice_similarity_boost);
  }

  return voiceConfig;
}
